import type * as React from 'react'

import { clsx } from 'clsx'
import { formatDistanceToNowStrict } from 'date-fns'
import { Link as RouterLink } from 'react-router-dom'

import { SplitProtected } from '@buffer-mono/features'
import {
  ChartColumnIcon,
  Button,
  ClockIcon,
  IconButton,
  RefreshCwIcon,
  Text,
  ScrollArea,
} from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { requiresRefreshing } from '~publish/components/PostCard/helpers'
import {
  usePostData,
  useTrackActionClicked,
} from '~publish/components/PostCard/PostCardContext'
import type {
  PostMetricType,
  PostMetricUnit,
} from '~publish/components/graphqlEnums'

type Metric = {
  type: PostMetricType
  name: string
  description?: string | null
  nullableValue?: number | null
  unit: PostMetricUnit
}
import { useIsAnalyzeUpgradePathEnabled } from '~publish/hooks/useIsAnalyzeUpgradePathEnabled'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'

import { PostMetric } from './PostMetric'
import { shouldShowMetricInfo } from './postMetricInfo'

import styles from './PostMetrics.module.css'

import type { JSX } from 'react'

export const PostMetricsWrapper = ({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}): JSX.Element | null => {
  const { schedulingType, status, metrics } = usePostData()

  // Notifications don't have metrics
  if (schedulingType === 'notification' || status !== 'sent' || !metrics) {
    return null
  }

  return (
    <section
      aria-label="Analytics"
      className={clsx(styles.metricsWrapper, className)}
    >
      {children}
    </section>
  )
}

type MoreAnalyticsButtonProps = {
  analyzeUrl: string
}

export const MoreAnalyticsButton = ({
  analyzeUrl,
}: MoreAnalyticsButtonProps): JSX.Element => {
  const trackPostAction = useTrackActionClicked()

  return (
    <Button
      variant="secondary"
      as="a"
      href={analyzeUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(): void => trackPostAction('seeMoreAnalytics-2')}
    >
      <ChartColumnIcon />
      More Analytics...
    </Button>
  )
}

export const MoreAnalyticsIconButton = ({
  analyzeUrl,
}: MoreAnalyticsButtonProps): JSX.Element => {
  const trackPostAction = useTrackActionClicked()
  const isAnalyzeUpgradePathEnabled = useIsAnalyzeUpgradePathEnabled()
  const openGlobalModal = useOpenGlobalModal()
  const onSeeMoreAnalyticsClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    trackPostAction('seeMoreAnalytics-2')
    if (isAnalyzeUpgradePathEnabled) {
      event.preventDefault()
      openGlobalModal(MODALS.analyzeUpgradePath, {
        cta: 'seeMoreAnalytics-2',
      })
    }
  }

  return (
    <IconButton
      label="See More Analytics"
      tooltip="See more analytics in Analyze"
      size="medium"
      as="a"
      href={analyzeUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onSeeMoreAnalyticsClick}
      className="analyzeCta"
    >
      <ChartColumnIcon />
    </IconButton>
  )
}

type InsightsIconButtonProps = {
  insightsUrl: string
}

export const InsightsIconButton = ({
  insightsUrl,
}: InsightsIconButtonProps): JSX.Element => {
  const trackPostAction = useTrackActionClicked()

  return (
    <IconButton
      label="See Post Insights"
      tooltip="See post insights"
      size="medium"
      as={RouterLink}
      to={insightsUrl}
      onClick={(): void => trackPostAction('seePostInsights-1')}
    >
      <ChartColumnIcon />
    </IconButton>
  )
}

export const PostMetricsScroller = ({
  metrics,
  engageUrl,
}: {
  metrics: Metric[]
  engageUrl: string | null
}): JSX.Element => {
  const { channel } = usePostData()

  return (
    <ScrollArea
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The scrollable metrics collection is exposed as one named group.
      role="group"
      aria-label="Metrics list"
      scrollbars="horizontal"
      visibility="none"
      className={styles.metricsScroller}
      gradientFade={50}
      showScrollButtons={true}
    >
      <div className={styles.metricsScrollerContent}>
        {metrics.map((metric) => (
          <PostMetric
            key={metric.name}
            type={metric.type}
            name={metric.name}
            description={
              shouldShowMetricInfo(channel.service, metric.type)
                ? metric.description
                : null
            }
            value={metric.nullableValue}
            unit={metric.unit}
            engageUrl={engageUrl}
          />
        ))}
      </div>
    </ScrollArea>
  )
}

export const MetricsRefresh = ({
  className,
}: {
  className?: string
}): JSX.Element | null => {
  const { metrics, metricsUpdatedAt, status } = usePostData()

  if (status !== 'sent' || !metrics) {
    // eslint-disable-next-line no-console
    console.log('debug: returning null for MetricsRefresh')
    return null
  }

  const hasNoData = metrics.every(
    (metric) => typeof metric.nullableValue !== 'number',
  )

  return (
    <SplitProtected name="CT-show-metric-refresh">
      {metricsUpdatedAt ? (
        <div className={clsx(styles.lastUpdated, className)}>
          <ClockIcon size="xsmall" color="subtle" />
          <Text size="sm" color="subtle">
            Refreshed{' '}
            {formatDistanceToNowStrict(metricsUpdatedAt, { addSuffix: true })}
          </Text>
        </div>
      ) : null}
      {!metricsUpdatedAt && hasNoData ? (
        <div className={clsx(styles.lastUpdated, className)}>
          <ClockIcon size="xsmall" color="subtle" />
          <Text size="sm" color="subtle">
            Metrics not available yet
          </Text>
        </div>
      ) : null}
    </SplitProtected>
  )
}

export const RefreshConnection = ({
  className,
}: {
  className?: string
}): JSX.Element | null => {
  const { channel, status } = usePostData()
  const refreshChannelConnection = useRefreshChannelConnection()
  const canManageChannels = useHasOrganizationAction('manageChannels')

  if (status !== 'sent' || !requiresRefreshing(channel)) {
    return null
  }

  const handleRefreshChannel = (cta: string): void => {
    if (canManageChannels) {
      refreshChannelConnection({
        channel,
        cta,
      })
    }
  }

  return (
    <section className={clsx(styles.refreshConnectionWrapper, className)}>
      <Text>Refresh your LinkedIn connection to unlock more metrics</Text>
      <Button
        variant="secondary"
        size="medium"
        disabled={!canManageChannels}
        onClick={(): void =>
          handleRefreshChannel(
            'publish-sentPosts-postMetrics-refreshConnection-1',
          )
        }
      >
        <RefreshCwIcon />
        Refresh Connection
      </Button>
    </section>
  )
}
